import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardActions,
    Button,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    ButtonGroup
} from '@mui/material';
import {
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    Inventory as InventoryIcon,
    Map as MapIcon,
    TableChart as TableChartIcon,
    PictureAsPdf as PdfIcon,
    TableView as ExcelIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function Dashboard() {
    const [companies, setCompanies] = useState([]);
    const [zones, setZones] = useState([]);
    const [filteredZones, setFilteredZones] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedZone, setSelectedZone] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [stats, setStats] = useState({
        totalCompanies: 0,
        totalZones: 0,
        totalAssets: 0,
        latestAssets: [],
        assetsPerCompany: [],
        assetsByCategory: [],
        recentActivity: []
    });
    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Filter zones when company changes
    useEffect(() => {
        if (selectedCompany) {
            const zonesInCompany = zones.filter(zone => String(zone.company_id) === String(selectedCompany));
            setFilteredZones(zonesInCompany);
            setSelectedZone(''); // Reset zone selection when company changes
        } else {
            setFilteredZones([]); // Show no zones when no company is selected
            setSelectedZone('');
        }
    }, [selectedCompany, zones]);

    // Fetch companies and zones for filtering
    useEffect(() => {
        const fetchFilterData = async () => {
            try {
                // Fetch companies
                const companiesResponse = await axios.get(`${API_BASE_URL}/companies/read.php`);
                const companiesData = companiesResponse.data.records || [];
                setCompanies(companiesData);

                // Fetch zones
                const zonesResponse = await axios.get(`${API_BASE_URL}/zones/read.php`);
                const zonesData = zonesResponse.data.records || [];
                setZones(zonesData);
            } catch (err) {
                console.error('Error fetching filter data:', err);
            }
        };

        fetchFilterData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Use a single API call to get all dashboard data
            const response = await axios.get(`${API_BASE_URL}/dashboard/stats.php`);
            
            if (!response.data || !response.data.data) {
                throw new Error('Invalid response data received from server');
            }

            const { total_companies, total_zones, total_assets, latest_assets, assets_per_company, assets_by_category, recent_activity } = response.data.data;

            // Update state with all data at once
            setStats({
                totalCompanies: total_companies,
                totalZones: total_zones,
                totalAssets: total_assets,
                latestAssets: latest_assets,
                assetsPerCompany: assets_per_company,
                assetsByCategory: assets_by_category,
                recentActivity: recent_activity
            });

            // Fetch companies and zones for reports functionality
            const [companiesRes, zonesRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/companies/read.php`),
                axios.get(`${API_BASE_URL}/zones/read.php`)
            ]);

            setCompanies(companiesRes.data.records || []);
            setZones(zonesRes.data.records || []);
            
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Simple Excel report generation
    const generateExcelReport = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Build API parameters - Fetch ALL assets by using a very large limit
            const params = new URLSearchParams({
                limit: 1000000, // Effectively "all"
                page: 1,
                _t: Date.now()
            });

            if (selectedCompany) {
                params.append('company_id', selectedCompany);
            }
            if (selectedZone) {
                params.append('zone_id', selectedZone);
            }

            // Fetch assets
            const response = await axios.get(`${API_BASE_URL}/assets/read.php?${params}`);
            const assets = response.data.data?.records || [];

            if (assets.length === 0) {
                setError('No assets found for the selected filters');
                return;
            }

            // V2: Generate Excel file with enhanced professional styling
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet([], { skipHeader: true }); // Create an empty sheet

            // --- Report Header ---
            const companyName = companies.find(c => c.id == selectedCompany)?.name || 'All Companies';
            const zoneName = zones.find(z => z.id == selectedZone)?.name || 'All Zones';

            const reportTitle = "Assets Inventory Report";
            const reportSubtitle = `${companyName} | ${zoneName}`;
            const generatedInfo = `Generated on: ${new Date().toUTCString()}`;

            // Add title and merge cells
            ws['A1'] = { v: reportTitle, t: 's', s: {
                font: { sz: 22, bold: true, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "002060" } }, // Dark blue
                alignment: { horizontal: 'center', vertical: 'center' }
            }};
            ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 11 } }]; // Span across 12 columns

            // Add subtitle and merge
            ws['A2'] = { v: reportSubtitle, t: 's', s: {
                font: { sz: 14, italic: true, color: { rgb: "E7E6E6" } },
                fill: { fgColor: { rgb: "002060" } },
                alignment: { horizontal: 'center', vertical: 'center' }
            }};
            ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 11 } });

            // Add generated info and merge
            ws['A3'] = { v: generatedInfo, t: 's', s: {
                font: { sz: 10, color: { rgb: "7F7F7F" } },
                alignment: { horizontal: 'right' }
            }};
            ws['!merges'].push({ s: { r: 2, c: 0 }, e: { r: 2, c: 11 } });

            // --- Summary Section ---
            ws['A5'] = { v: 'Report Summary', t: 's', s: {
                font: { sz: 14, bold: true, color: { rgb: "002060" } }
            }};
            ws['!merges'].push({ s: { r: 4, c: 0 }, e: { r: 4, c: 2 } });
            
            ws['A6'] = { v: 'Total Assets', t: 's', s: { font: { bold: true } }};
            ws['B6'] = { v: assets.length, t: 'n', s: {
                font: { sz: 12, bold: true, color: { rgb: "0070C0" } },
                alignment: { horizontal: 'left' }
            }};

            // --- Data Table ---
            const tableHeaders = ['Asset ID', 'Asset Name', 'FA Number', 'Barcode', 'Serial Number', 'Supplier', 'Unit', 'Category', 'Description', 'Condition', 'Zone', 'Company'];
            const excelData = assets.map(asset => ({
                'Asset ID': asset.id || '', 'Asset Name': asset.name || '', 'FA Number': asset.fa_number || '',
                'Barcode': asset.barcode || '', 'Serial Number': asset.serial_number || '', 'Supplier': asset.supplier || '',
                'Unit': asset.unit || '', 'Category': asset.category || '', 'Description': asset.description || '',
                'Condition': asset.condition || '', 'Zone': asset.zone_name || '', 'Company': asset.company_name || ''
            }));
            
            XLSX.utils.sheet_add_json(ws, excelData, { origin: 'A8', skipHeader: true });

            // Style Table Header
            tableHeaders.forEach((header, i) => {
                ws[XLSX.utils.encode_cell({c: i, r: 7})] = { v: header, t: 's', s: {
                    font: { sz: 12, bold: true, color: { rgb: "FFFFFF" } },
                    fill: { fgColor: { rgb: "0070C0" } }, // Bright blue
                    alignment: { horizontal: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: 'thin', color: { rgb: '000000' } },
                        right: { style: 'thin', color: { rgb: '000000' } }
                    }
                }};
            });

            // Add alternating row colors and borders to data
            for (let R = 8; R < 8 + assets.length; ++R) {
                const isEven = (R - 8) % 2 === 0;
                for (let C = 0; C < tableHeaders.length; ++C) {
                    const cell = ws[XLSX.utils.encode_cell({c: C, r: R})];
                    if(cell) {
                        const cellStyle = {
                            fill: { fgColor: { rgb: isEven ? "DDEBF7" : "FFFFFF" } }, // Light blue / White
                            border: {
                                top: { style: 'thin', color: { rgb: 'B2B2B2' } },
                                bottom: { style: 'thin', color: { rgb: 'B2B2B2' } },
                                left: { style: 'thin', color: { rgb: 'B2B2B2' } },
                                right: { style: 'thin', color: { rgb: 'B2B2B2' } }
                            },
                            alignment: { vertical: 'top' }
                        };

                        if (C === 8) { // Apply text wrap to the Description column
                            cellStyle.alignment.wrapText = true;
                        }
                        
                        cell.s = cellStyle;
                    }
                }
            }

            // Set column widths
            ws['!cols'] = [
                { wch: 10 }, { wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 25 },
                { wch: 30 }, { wch: 15 }, { wch: 25 }, { wch: 50 }, { wch: 15 }, { wch: 30 }, { wch: 30 }
            ];
            
            // --- Enable AutoFilter and Freeze Panes for a more responsive/interactive report ---
            const tableRange = `A7:${XLSX.utils.encode_col(tableHeaders.length - 1)}${assets.length + 7}`;
            ws['!autofilter'] = { ref: tableRange };
            ws['!freeze'] = { ySplit: 8 }; // Freeze rows 1-8
            
            XLSX.utils.book_append_sheet(wb, ws, 'Assets Report');

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
            const filename = `ASSETS_REPORT_${companyName.replace(/\s+/g, '_')}_${zoneName.replace(/\s+/g, '_')}_${timestamp}.xlsx`;

            XLSX.writeFile(wb, filename);
            setSuccess('Professional Excel report generated successfully!');

        } catch (error) {
            console.error('Error generating Excel report:', error);
            setError('Failed to generate Excel report');
        } finally {
            setLoading(false);
        }
    };

    // Simple PDF report generation
    const generatePDFReport = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Build API parameters - Fetch ALL assets
            const params = new URLSearchParams({
                limit: 1000000,
                page: 1,
                _t: Date.now()
            });

            if (selectedCompany) {
                params.append('company_id', selectedCompany);
            }
            if (selectedZone) {
                params.append('zone_id', selectedZone);
            }

            // Fetch assets
            const response = await axios.get(`${API_BASE_URL}/assets/read.php?${params}`);
            const assets = response.data.data?.records || [];

            if (assets.length === 0) {
                setError('No assets found for the selected filters');
                return;
            }

            // Create PDF with a professional, responsive design
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            // Get company and zone names
            const companyName = companies.find(c => c.id == selectedCompany)?.name || 'All Companies';
            const zoneName = zones.find(z => z.id == selectedZone)?.name || 'All Zones';
            
            // Add header
            doc.setFillColor(22, 36, 52); // Dark blue-gray
            doc.rect(0, 0, 297, 28, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text('ASSETS INVENTORY REPORT', 148.5, 15, { align: 'center' });
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(`${companyName} | ${zoneName}`, 148.5, 22, { align: 'center' });
            
            // Add summary info
            doc.setTextColor(31, 41, 55);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toUTCString()}`, 15, 38);
            doc.text(`Total Assets Found: ${assets.length}`, 282, 38, { align: 'right' });
            
            // Prepare table data
            const head = [['Barcode', 'Asset Name', 'FA Number', 'Serial Number', 'Category', 'Condition', 'Zone', 'Company']];
            const body = assets.map(asset => [
                asset.barcode,
                asset.name,
                asset.fa_number,
                asset.serial_number,
                asset.category,
                asset.condition || '',
                asset.zone_name,
                asset.company_name
            ]);
            
            // Add table using autoTable for a professional look
            doc.autoTable({
                head: head,
                body: body,
                startY: 45,
                theme: 'grid',
                headStyles: {
                    fillColor: [41, 128, 185], // Professional blue
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                styles: {
                    cellPadding: 2,
                    fontSize: 8
                }
            });
            
            // Add footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFillColor(22, 36, 52);
                doc.rect(0, 200, 297, 10, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(8);
                doc.text(`Page ${i} of ${pageCount}`, 148.5, 206, { align: 'center' });
            }
            
            // Generate filename
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
            const filename = `ASSETS_REPORT_${companyName.replace(/\s+/g, '_')}_${zoneName.replace(/\s+/g, '_')}_${timestamp}.pdf`;
            
            doc.save(filename);
            setSuccess('Professional PDF report generated successfully!');

        } catch (error) {
            console.error('Error generating PDF report:', error);
            setError('Failed to generate PDF report');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ ml: 2 }}>
                        Loading dashboard data...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button 
                    variant="contained" 
                    onClick={fetchDashboardData}
                    sx={{ mt: 2 }}
                >
                    Retry Loading Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Stats Cards */}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: { xs: 120, sm: 140 },
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: 4
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                            <BusinessIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 30 } }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                Total Companies
                            </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '3rem' } }}>
                            {stats.totalCompanies}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: { xs: 120, sm: 140 },
                            bgcolor: 'secondary.main',
                            color: 'white',
                            borderRadius: 4
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                            <LocationIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 30 } }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                Total Zones
                            </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '3rem' } }}>
                            {stats.totalZones}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: { xs: 120, sm: 140 },
                            bgcolor: 'success.main',
                            color: 'white',
                            borderRadius: 4
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                            <InventoryIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 30 } }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                Total Assets
                            </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '3rem' } }}>
                            {stats.totalAssets}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Reports Section for Admins */}
                {isAdmin && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                Generate Assets Report
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Stack spacing={{ xs: 2, sm: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="report-type-label">Report Type</InputLabel>
                                    <Select
                                        value={selectedCompany}
                                        label="Company"
                                        labelId="report-type-label"
                                        id="report-type-select"
                                        name="reportType"
                                        onChange={(e) => setSelectedCompany(e.target.value)}
                                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                                    >
                                        <MenuItem value="">All Companies</MenuItem>
                                        {companies.map((company) => (
                                            <MenuItem key={company.id} value={company.id}>
                                                {company.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedCompany && (
                                    <FormControl fullWidth>
                                        <InputLabel>Zone</InputLabel>
                                        <Select
                                            value={selectedZone}
                                            onChange={(e) => setSelectedZone(e.target.value)}
                                            label="Zone"
                                        >
                                            <MenuItem value="">All Zones</MenuItem>
                                            {filteredZones.map((zone) => (
                                                <MenuItem key={zone.id} value={zone.id}>
                                                    {zone.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Add Report Generation Buttons */}
            <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 1, sm: 2 } }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Generate Reports
                </Typography>
                <ButtonGroup 
                    variant="contained" 
                    sx={{ 
                        mb: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        '& .MuiButton-root': {
                            borderRadius: { xs: '4px !important', sm: 'inherit' },
                            mb: { xs: 1, sm: 0 }
                        }
                    }}
                >
                    <Button
                        fullWidth
                        startIcon={<ExcelIcon />}
                        onClick={generateExcelReport}
                        disabled={loading}
                    >
                        Export to Excel
                    </Button>
                    <Button
                        fullWidth
                        startIcon={<PdfIcon />}
                        onClick={generatePDFReport}
                        disabled={loading}
                    >
                        Export to PDF
                    </Button>
                </ButtonGroup>
            </Box>
        </Container>
    );
}

export default Dashboard;